import { type V1ApiResponse } from '@webapp/core/interfaces/api/V1ApiResponse'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type IPODisclaimerRepository,
  type PODisclaimerLabelListParams,
  type PODisclaimerLabelPayload,
} from '@webapp/modules/purchasing/business-logic/interfaces/IPODisclaimerRepository'
import { type PODisclaimerLabelConstructor } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { type PODisclaimerLabelApiModel } from '../../api-models/PODisclaimerLabelApiModel'
import { mapPODisclaimerLabelApiModel } from '../../mappers/mapPODisclaimerLabelApiModel'

// V1 validation failures arrive as HTTP 200 with `success: false` and
// field-error arrays under `data.<field>` (e.g. `{ description: [...] }`)
// or `data.__all__`.
interface V1ErrorData {
  __all__?: string[]
  [key: string]: string[] | undefined
}

const getFirstV1ErrorMessage = (data: unknown): string => {
  const errorData = data as V1ErrorData | null

  if (errorData?.__all__?.[0]) {
    return errorData.__all__[0]
  }

  const firstFieldErrors = Object.values(errorData ?? {}).find(
    (fieldErrors) => Array.isArray(fieldErrors) && fieldErrors.length > 0
  )

  return firstFieldErrors?.[0] ?? ''
}

const throwRepoErrorFromApiV1Error = (error: unknown): never => {
  if (error instanceof HttpError) {
    const errorBody = error.response as { data?: V1ErrorData } | null

    throw new RepoError({
      code: null,
      message: getFirstV1ErrorMessage(errorBody?.data),
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }

  throw error
}

const throwIfV1Failure = (success: boolean, data: unknown): void => {
  if (!success) {
    throw new RepoError({
      code: null,
      message: getFirstV1ErrorMessage(data),
      status: 400,
      response: data,
    })
  }
}

interface Dependency {
  httpClient: IHttpClient
}

export class PODisclaimerRepository implements IPODisclaimerRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params: PODisclaimerLabelListParams
  ): Promise<RepoPaginatedResponse<PODisclaimerLabelConstructor>> {
    try {
      const res = await this.deps.httpClient.get<
        V1ApiResponse<PODisclaimerLabelApiModel[]>
      >(`/api/settings/disclaimer/`, { params })

      throwIfV1Failure(res.data.success, res.data.data)

      const data = res.data.data.map((item) =>
        mapPODisclaimerLabelApiModel(item)
      )

      // The V1 envelope carries no pagination metadata; synthesize it from
      // the requested page — a full page implies at least one more page.
      const numPages =
        data.length === params.page_size ? params.page + 1 : params.page

      return {
        data,
        pagination: {
          count: data.length,
          page: params.page,
          pageSize: params.page_size,
          numPages,
        },
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async create(payload: PODisclaimerLabelPayload): Promise<void> {
    try {
      const res = await this.deps.httpClient.post<V1ApiResponse<unknown>>(
        `/api/settings/disclaimer/`,
        {
          id: null,
          description: payload.description,
          text: payload.text,
        }
      )

      throwIfV1Failure(res.data.success, res.data.data)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async update(id: number, payload: PODisclaimerLabelPayload): Promise<void> {
    try {
      const res = await this.deps.httpClient.put<V1ApiResponse<unknown>>(
        `/api/settings/disclaimer/${id}/`,
        {
          id,
          description: payload.description,
          text: payload.text,
        }
      )

      throwIfV1Failure(res.data.success, res.data.data)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async delete(id: number): Promise<void> {
    try {
      // The delete endpoint answers 200 with an empty body — no envelope
      // parsing (failures arrive as HTTP errors)
      await this.deps.httpClient.delete(`/api/settings/disclaimer/${id}/`)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }
}
